import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Download, Loader2, Mail, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const when = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function SignupsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = () => api.get("/admin/signups").then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load sign-up settings"));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data?.notifying) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data?.notifying]);

  const toggle = async (open) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/signups", { open });
      setData(data);
      toast.success(open ? "Sign-ups are open" : "Sign-ups are paused");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update sign-ups");
    } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/waitlist.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "finlo-waitlist.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't export the waitlist"); }
  };

  const notify = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/notify");
      setData(data);
      toast.success(`Sending to ${data.queued} ${data.queued === 1 ? "person" : "people"}. This page updates as emails go out.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't email the waitlist");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="h-48 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>;

  return (
    <div className="space-y-5" data-testid="signups-panel">
      <div className="mm-glow-card p-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">New sign-ups</p>
          <p data-testid="signups-status-text" className="text-sm text-slate-400 mt-0.5">
            {data.open ? "Open: anyone can create an account." : "Paused: visitors see \u201cComing soon\u201d and can join the waitlist."}
          </p>
        </div>
        <Switch data-testid="signups-toggle" checked={data.open} disabled={busy} onCheckedChange={toggle} className="data-[state=checked]:bg-[#00C896]" />
      </div>

      <div className="mm-glow-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-[#00C896]/12 border border-[#00C896]/25 flex items-center justify-center"><Users className="h-5 w-5 text-[#00C896]" /></span>
            <div>
              <p className="font-num text-2xl font-extrabold leading-none" data-testid="waitlist-count">{data.waitlist_count}</p>
              <p className="text-xs text-slate-400 mt-1" data-testid="waitlist-pending-count">on the waitlist · {data.pending_count} not emailed yet</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="waitlist-export-button" onClick={exportCsv} disabled={!data.waitlist_count}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-40 transition-colors">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button data-testid="waitlist-notify-button" onClick={() => setConfirm(true)} disabled={busy || data.notifying || !data.open || !data.pending_count}
              title={data.open ? "" : "Open sign-ups first"}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#00C896] hover:bg-[#00E5AC] px-3.5 py-1.5 text-sm font-semibold text-[#04140D] disabled:bg-white/10 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors">
              <Mail className="h-4 w-4" /> Email the waitlist
            </button>
          </div>
        </div>
        {!data.open && data.pending_count > 0 && <p className="text-xs text-slate-500 mt-3">Open sign-ups to email everyone that Finlo is ready.</p>}
        {data.notifying && <p data-testid="waitlist-sending" className="text-xs text-[#4DE3BF] mt-3 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending emails…</p>}
        {!data.notifying && data.last_notify && (
          <p data-testid="waitlist-last-notify" className="text-xs text-slate-500 mt-3">
            Last send {when(data.last_notify.finished_at)}: {data.last_notify.sent} emailed{data.last_notify.failed ? `, ${data.last_notify.failed} couldn't be delivered` : ""}.
          </p>
        )}

        <div className="mt-4 divide-y divide-white/6 max-h-96 overflow-y-auto" data-testid="waitlist-list">
          {data.entries.map((e, i) => (
            <div key={e.email} data-testid={`waitlist-row-${i}`} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="truncate">{e.email}</span>
              <span className="shrink-0 flex items-center gap-2 text-xs text-slate-400">
                {e.notified_at && <span className="rounded-full border border-[#00C896]/30 bg-[#00C896]/10 px-1.5 py-0.5 text-[10px] text-[#00C896]">Emailed</span>}
                {when(e.joined_at)}
              </span>
            </div>
          ))}
          {data.entries.length === 0 && <p data-testid="waitlist-empty" className="py-6 text-center text-sm text-slate-500">No one has joined the waitlist yet.</p>}
        </div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="waitlist-notify-dialog" className="bg-[#14181D] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Email the waitlist?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {data.pending_count} {data.pending_count === 1 ? "person" : "people"} will get a real email saying Finlo sign-ups are open. People already emailed won't get it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="waitlist-notify-cancel" className="bg-transparent border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="waitlist-notify-confirm" onClick={notify} className="bg-[#00C896] text-[#04140D] hover:bg-[#00E5AC]">Send emails</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
